import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import useU2000AxiosAuthenticated from "../u2000Core/axios/U2000UseAxiosAuthenticated";
import { Outlet } from "react-router-dom";
import i18next from "i18next";
import { useU2000InitLUHandlers } from "../u2000Core/hooks/handler/U2000UseInitLUHandlers";
import { U2000HandleApiError, type U2000ApiErrorResult } from "../u2000Core/axios/U2000HandleApiError";
import { useToast } from "../u2000Core/context/toast/U2000ToastContext";
import { doInitLU, luHandleAppiError, type U3120Country, type U3120CustomerType, type U3120State } from "./U3120Service";
import { U3120ContextProvider } from "./U3120Context";
import { useU2000LogicalUnitContext } from "../u2000Core/context/logicalUnit/U2000LogicalUnitContext";
import { NS } from "./U3120Constants";

export { NS } from "./U3120Constants";

export const U3120ROLES = {
  READ: "U3120:READ",
  ADD: "U3120:ADD",
  EDIT: "U3120:EDIT",
  DELETE: "U3120:DELETE",
  AUDITS: "U3120:AUDITS",
  EXPORT: "U3120:EXPORT",
  /** Action pour l’onglet Taux horaire client : Ajouter, Modifier, Supprimer. */
  VIEWEMPLOYEERATE: "U3120:VIEWEMPLOYEERATE",
} as const;
export type U3120ROLES = (typeof U3120ROLES)[keyof typeof U3120ROLES];

export interface U3120InitLU {
  launchSearch: boolean;
  hideSearch: boolean;
  nbRecordPerPage: number;
  customerTypes?: U3120CustomerType[];
  defaultCountry?: U3120Country[];
  defaultState?: U3120State[];
}

const U3120: React.FC = () => {
  const { i18n } = useTranslation();
  const [i18nReady, setI18nReady] = useState<boolean>(false);
  const { t } = useTranslation();
  const toast = useToast();
  const [initLUReady, setInitLUReady] = useState<boolean>(false);
  const u2000AxiosAuthenticated = useU2000AxiosAuthenticated();
  const [initLUData, setInitLUData] = useState<U3120InitLU>({ launchSearch: false, hideSearch: true, nbRecordPerPage: 50 });
  // charge fichiers de traduction
  useEffect(() => {
    i18next.loadNamespaces(["u3120"], (err) => {
      if (err) return console.error("Error loading namespace:", err);
      setI18nReady(true);
    });
  }, [i18n]);

  // Gestion de l'initialisation de l'unité logique
  const handleInitLU = useU2000InitLUHandlers<U3120InitLU>({
    doInitLUFn: async () => {
      return await doInitLU(u2000AxiosAuthenticated);
    },
    onSuccess: (initLU: U3120InitLU) => {
      setInitLUData(initLU);
      setInitLUReady(true);
    },
    onError: (error: any) => {
      const errorResult = U2000HandleApiError(error, t, NS, luHandleAppiError) as U2000ApiErrorResult;
      if (errorResult.genericErrors?.length) {
        errorResult.genericErrors.forEach((err) => toast.error(err.text, err.title));
      }
      setInitLUReady(true);
    },
  });

  // Initialisation de l'unité logique au montage du composant et apres quelques changements de langue
  useEffect(() => {
    handleInitLU.getInitLU();
  }, [t]);

  // Set LU title and breadcrumb
  const { setLogicalUnitTitle, setLogicalUnitBreadcrumb } = useU2000LogicalUnitContext();
  useEffect(() => {
    if (!i18nReady || !initLUReady) return;
    const title = t("title", { ns: NS });
    setLogicalUnitTitle(title);

    return () => {
      setLogicalUnitTitle(null);
      setLogicalUnitBreadcrumb([]);
    };
  }, [i18nReady, initLUReady, t, setLogicalUnitTitle, setLogicalUnitBreadcrumb]);

  if (!i18nReady || !initLUReady) return null;

  return (
    <U3120ContextProvider initLU={initLUData}>
      <Outlet />
    </U3120ContextProvider>
  );
};

export default U3120;
